// One language of the "Загрузить готовое аудио" form: its own source — audio only (recognized),
// audio + text (aligned) or text only (voiced). The file goes straight into the bucket.
import type { Lang, UploadMode } from '@fonus/shared';
import { CheckCircle2, FileAudio, Loader2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { duration, LANG_LABEL } from '../lib/format.ts';
import { humanFromApi } from '../lib/errors.ts';
import {
  ACCEPT,
  audioDuration,
  durationNote,
  fileProblem,
  uploadNarration,
} from '../lib/upload.ts';
import { cn } from '../lib/utils.ts';
import { AutoTextarea, inputClass } from './common.tsx';
import { Button } from './ui/button.tsx';
import { Progress } from './ui/progress.tsx';

export interface UploadedFile {
  name: string;
  size: number;
  durationSec: number | null;
  key: string | null;
  progress: number;
  error: string | null;
}
export interface UploadLangValue {
  mode: UploadMode;
  title: string;
  summary: string;
  text: string;
  file: UploadedFile | null;
}
export const emptyUploadLang = (mode: UploadMode = 'audio'): UploadLangValue => ({
  mode,
  title: '',
  summary: '',
  text: '',
  file: null,
});

const MODES: { id: UploadMode; label: string; hint: string }[] = [
  { id: 'audio', label: 'Только аудио', hint: 'Текст распознаем и покажем для вычитки' },
  { id: 'audio_text', label: 'Аудио + текст', hint: 'Сверим текст с записью по словам' },
  { id: 'text', label: 'Только текст', hint: 'Озвучим выбранным голосом' },
];

export function isUploadLangReady(v: UploadLangValue): boolean {
  if (v.title.trim().length < 2 || v.summary.trim().length < 10) return false;
  if (v.mode !== 'audio' && v.text.trim().length < 200) return false;
  if (v.mode !== 'text' && !v.file?.key) return false;
  return true;
}

const mb = (n: number) => `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`;

export function UploadLang({
  lang,
  value,
  onChange,
  onTitleBlur,
}: {
  lang: Lang;
  value: UploadLangValue;
  /** Updater form: progress callbacks arrive after several re-renders. */
  onChange: (update: (prev: UploadLangValue) => UploadLangValue) => void;
  onTitleBlur: (title: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<UploadLangValue>) => onChange((prev) => ({ ...prev, ...patch }));
  const setFile = (patch: Partial<UploadedFile>) =>
    onChange((prev) => (prev.file ? { ...prev, file: { ...prev.file, ...patch } } : prev));

  const pick = async (file: File) => {
    const problem = fileProblem(file);
    set({
      file: {
        name: file.name,
        size: file.size,
        durationSec: null,
        key: null,
        progress: 0,
        error: problem,
      },
    });
    if (problem) return;
    setBusy(true);
    try {
      setFile({ durationSec: await audioDuration(file) });
      const key = await uploadNarration(lang, file, (p) => setFile({ progress: p }));
      setFile({ key, progress: 1 });
    } catch (err) {
      setFile({ error: humanFromApi(err).title });
    } finally {
      setBusy(false);
    }
  };

  const f = value.file;
  const note = durationNote(f?.durationSec ?? null);
  return (
    <fieldset
      className="flex flex-col gap-3 rounded-[16px] border border-line p-4"
      data-lang={lang}
    >
      <legend className="px-1 text-[14px] font-semibold">{LANG_LABEL[lang]}</legend>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={`Источник ${lang}`}>
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={value.mode === m.id}
            title={m.hint}
            className={cn(
              'rounded-full border px-3 py-1.5 text-[13px] font-semibold transition',
              value.mode === m.id
                ? 'border-accent bg-accent-soft text-accent-strong'
                : 'border-line text-muted hover:text-fg',
            )}
            onClick={() => set({ mode: m.id })}
          >
            {m.label}
          </button>
        ))}
      </div>
      <p className="text-[13px] text-muted">{MODES.find((m) => m.id === value.mode)?.hint}</p>
      <input
        className={inputClass}
        placeholder="Название"
        value={value.title}
        onChange={(e) => set({ title: e.target.value })}
        onBlur={(e) => onTitleBlur(e.target.value)}
      />
      <AutoTextarea
        placeholder="Кратко для родителей"
        value={value.summary}
        onChange={(e) => set({ summary: e.target.value })}
      />
      {value.mode !== 'text' && (
        <div className="flex flex-col gap-2 rounded-[12px] bg-fill/60 p-3">
          <input
            ref={input}
            type="file"
            accept={ACCEPT}
            className="hidden"
            aria-label={`Аудио ${lang}`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void pick(file);
              e.target.value = '';
            }}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => input.current?.click()}
            >
              {busy ? <Loader2 className="animate-spin" /> : <Upload />}
              {f ? 'Заменить файл' : 'Выбрать файл'}
            </Button>
            <span className="text-[13px] text-muted">mp3, wav или m4a, до 200 МБ</span>
          </div>
          {f && (
            <div className="flex flex-col gap-1.5 text-[14px]">
              <div className="flex flex-wrap items-center gap-2">
                {f.key ? (
                  <CheckCircle2 className="size-4 text-ok" />
                ) : (
                  <FileAudio className="size-4 text-muted" />
                )}
                <span className="font-medium">{f.name}</span>
                <span className="text-muted">
                  {mb(f.size)}
                  {f.durationSec !== null && ` · ${duration(Math.round(f.durationSec))}`}
                </span>
              </div>
              {!f.key && !f.error && <Progress value={f.progress * 100} />}
              {f.error && <p className="text-[13px] text-bad">{f.error}</p>}
              {note && <p className="text-[13px] text-warn">{note}</p>}
            </div>
          )}
        </div>
      )}
      {value.mode !== 'audio' && (
        <AutoTextarea
          placeholder={
            value.mode === 'audio_text'
              ? 'Текст, который звучит в записи (слово в слово)'
              : 'Текст (абзацы — через пустую строку)'
          }
          value={value.text}
          onChange={(e) => set({ text: e.target.value })}
        />
      )}
    </fieldset>
  );
}
